/* eslint-disable testing-library/no-unnecessary-act */
import { useEffect, useState, useCallback } from 'react';
import { ListingSearchCriteria } from '@components/layouts/Truva/ListingSearchDeprecated/utils';
import { useTranslation } from 'react-i18next';
import Input from '@components/shared/Form/Input';
import { useDispatch } from 'react-redux';
import useDebounce from '@shared/useDebounce';
import { ListingSearchFilterTypes } from '@root/types';
import actions from '@store/actions/listingSearchPage';
import { debounce } from 'lodash';
import s from './SizeFilter.module.less';

type Props = {
  criteria: ListingSearchCriteria;
  onChange: (criteria: () => ListingSearchCriteria) => void;
  type: ListingSearchFilterTypes;
};

const debounceLength = 1000;

const SizeFilters = ({ criteria, onChange, type }: Props) => {
  const [sizeFilter, setSizeFilter] = useState(criteria.sizes());

  const debouncedCriteria = useDebounce(criteria, debounceLength);

  const { t } = useTranslation('filters');
  const dispatch = useDispatch();

  const dispatchAnalytics = criteria => {
    const newSizeFilter = criteria.sizes();
    dispatch(
      actions.sizeFilterChanged(
        `${newSizeFilter.minSize.length > 0 ? newSizeFilter.minSize : 0}-${
          newSizeFilter.maxSize.length > 0 ? newSizeFilter.maxSize : 0
        }`,
      ),
    );
    return criteria;
  };

  const updateCriteria = useCallback(
    debounce(newSizeFilter => {
      onChange(() => {
        criteria.changeSizeFilter(newSizeFilter);
        dispatchAnalytics(criteria);
        return criteria;
      });
    }, debounceLength),
    [criteria],
  );

  const onInputChanged = (sizeValue: string, minOrMax: string) => {
    setSizeFilter(oldSizeFilter => {
      const newSizeFilter = {
        ...oldSizeFilter,
        [minOrMax]: sizeValue,
      };

      updateCriteria.cancel();
      updateCriteria(newSizeFilter);

      return newSizeFilter;
    });
  };

  useEffect(() => {
    const sizes = criteria.sizes();

    // the only time we want to react to this is when clearing
    if (sizes.minSize === '' && sizes.maxSize === '') {
      setSizeFilter(sizes);
    }
  }, [debouncedCriteria]);

  return (
    <>
      <div className={s.rangeInputContainer}>
        <Input
          labelText={t('size.minSize')}
          inputClassName={s.input}
          name={`minSize-${type}`}
          type="number"
          value={sizeFilter.minSize}
          onChange={(sizeValue: string) => onInputChanged(sizeValue, 'minSize')}
        />
        <span className={s.rangeUnit}>{t('size.sf')}</span>
      </div>
      <div className={s.rangeInputContainer}>
        <Input
          labelText={t('size.maxSize')}
          inputClassName={s.input}
          name={`maxSize-${type}`}
          type="number"
          value={sizeFilter.maxSize}
          onChange={(sizeValue: string) => onInputChanged(sizeValue, 'maxSize')}
        />
        <span className={s.rangeUnit}>{t('size.sf')}</span>
      </div>
    </>
  );
};

export default SizeFilters;
